import { createTheme, ThemeOptions } from "@mui/material/styles";
import { PaletteOptions } from "@mui/material";

// Professional educational palette
export const PRIMARY = "#800020"; // Burgundy - primary action color (elegant and sophisticated)
export const SECONDARY = "#1D3557"; // Darker blue - secondary actions
export const ACCENT = "#8b7355"; // Brown - accents and highlights
export const ACCENT_SECONDARY = "#E63946"; // Refined red - for emphasis and important elements

// Neutrals
export const NEUTRAL_BLACK = "#1B1C20";
export const NEUTRAL_DARK = "#2B2D42"; // Dark text and backgrounds
export const NEUTRAL_MEDIUM = "#8D99AE"; // Medium emphasis text
export const NEUTRAL_LIGHT = "#EDF2F4"; // Light backgrounds
export const NEUTRAL_WHITE = "#FFFFFF";

// Feedback colors
export const SUCCESS = "#4CAF50";
export const WARNING = "#FF9800";
export const ERROR = "#F72C25";
export const INFO = "#2196F3";

// Theory and Rules colors
export const THEORY_PRIMARY = "#146173";
export const THEORY_SECONDARY = "#89B0B9";
export const THEORY_BACKGROUND = "#DCEAEE";

export const RULES_PRIMARY = "#BB7128";
export const RULES_SECONDARY = "#C09144";
export const RULES_BACKGROUND = "#F1E3D4";

// Define the palette options
const palette: PaletteOptions = {
  primary: {
    main: PRIMARY,
    contrastText: NEUTRAL_WHITE,
  },
  secondary: {
    main: SECONDARY,
    contrastText: NEUTRAL_WHITE,
  },
  error: {
    main: ERROR,
    light: "#FF4D7B",
    dark: "#800022",
    contrastText: NEUTRAL_WHITE,
  },
  warning: {
    main: WARNING,
    contrastText: NEUTRAL_BLACK,
  },
  info: {
    main: INFO,
    contrastText: NEUTRAL_WHITE,
  },
  success: {
    main: SUCCESS,
    contrastText: NEUTRAL_WHITE,
  },
  text: {
    primary: NEUTRAL_BLACK,
    secondary: NEUTRAL_DARK,
    disabled: NEUTRAL_MEDIUM,
  },
  background: {
    default: NEUTRAL_WHITE,
    paper: NEUTRAL_LIGHT,
  },
  divider: NEUTRAL_MEDIUM,
};

// Define the theme options
export const themeOptions: ThemeOptions = {
  palette,
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: "2rem", // 32px
      fontFamily: '"Inter", sans-serif',
      fontWeight: 700,
      color: PRIMARY,
      letterSpacing: "0.5px",
      marginBottom: "1rem",
    },
    h2: {
      fontSize: "1.625rem", // 26px
      fontFamily: '"Inter", sans-serif',
      fontWeight: 700,
      color: PRIMARY,
      letterSpacing: "0.3px",
      marginBottom: "0.75rem",
    },
    h3: {
      fontSize: "1.375rem", // 22px
      fontFamily: '"Inter", sans-serif',
      fontWeight: 700,
      color: PRIMARY,
      letterSpacing: "0.2px",
      marginBottom: "0.625rem",
    },
    h4: {
      fontSize: "1.125rem", // 18px
      fontFamily: '"Inter", sans-serif',
      fontWeight: 700,
      color: PRIMARY,
      marginBottom: "0.5rem",
    },
    h5: {
      fontSize: "1rem", // 16px
      fontFamily: '"Inter", sans-serif',
      fontWeight: 700,
      color: NEUTRAL_DARK,
      marginBottom: "0.375rem",
    },
    h6: {
      fontSize: "0.875rem", // 14px
      fontFamily: '"Inter", sans-serif',
      fontWeight: 700,
      color: NEUTRAL_DARK,
    },
    body1: {
      fontSize: "1rem", // 16px
      fontFamily: '"Zilla Slab", serif',
      color: NEUTRAL_DARK,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: "0.875rem", // 14px
      fontFamily: '"Zilla Slab", serif',
      color: NEUTRAL_BLACK,
      lineHeight: 1.43,
    },
    subtitle1: {
      fontSize: "1.125rem", // 18px
      fontFamily: '"Zilla Slab", serif',
      color: NEUTRAL_DARK,
      lineHeight: 1.75,
    },
    subtitle2: {
      fontSize: "0.75rem", // 12px
      fontFamily: '"Zilla Slab", serif',
      color: NEUTRAL_BLACK,
      lineHeight: 1.57,
    },
    button: {
      fontSize: "0.875rem", // 14px
      fontFamily: '"Montserrat", sans-serif',
      fontWeight: 700,
      textTransform: "none",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          padding: "8px 16px",
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
          },
        },
        containedPrimary: {
          backgroundColor: PRIMARY,
          "&:hover": {
            backgroundColor: "#600018", // Darker burgundy
          },
        },
        containedSecondary: {
          backgroundColor: SECONDARY,
          "&:hover": {
            backgroundColor: "#142841", // Darker blue
          },
        },
        outlined: {
          borderWidth: 2,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: NEUTRAL_WHITE,
          color: PRIMARY,
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: NEUTRAL_MEDIUM,
            },
            "&:hover fieldset": {
              borderColor: PRIMARY,
            },
            "&.Mui-focused fieldset": {
              borderColor: PRIMARY,
            },
          },
        },
      },
    },
  },
  shape: {
    borderRadius: 4,
  },
};

// Create the actual theme
const theme = createTheme(themeOptions);

export default theme;
